import { HierarchyLabelType } from 'app-config';
import axios from 'axios';

const BASE_URL = process.env.REACT_APP_HIERARCHY_LABEL_API_URL as string;

const getOAuthToken = (): string => localStorage.getItem('oauth_token') || '';

/**
 * Refreshes the OAuth token by calling the auth service.
 * This is the same logic used in AuthStore.fetchOAuthToken.
 */
const refreshOAuthToken = async (): Promise<boolean> => {
	try {
		const urlhost = window.location.host;
		let oAuthUrl = 'https://auth-dev.venzagrc.com/auth/gettoken'; // Default to dev for localhost

		if (urlhost.includes('tg.') || urlhost.includes('stg.')) {
			oAuthUrl = 'https://auth-stage.venzagrc.com/auth/gettoken';
		} else if (
			!urlhost.includes('ev.') &&
			!urlhost.includes('tg.') &&
			!urlhost.includes('stg.') &&
			!urlhost.includes('192') &&
			!urlhost.includes('localhost')
		) {
			oAuthUrl = 'https://auth-prod.venzagrc.com/auth/gettoken';
		}

		const formData = new FormData();
		const peakid = localStorage.getItem('peakid');
		if (peakid) {
			formData.append('peakid', peakid);
		}

		const response = await axios.post(oAuthUrl, formData);
		localStorage.setItem('oauth_token', response.data);
		return true;
	} catch (error) {
		console.error('Failed to refresh OAuth token:', error);
		return false;
	}
};

const orgDirectoryFetch = async (
	url: string,
	options: RequestInit = {}
): Promise<Response> => {
	const makeRequest = () =>
		fetch(url, {
			...options,
			headers: {
				'Content-Type': 'application/json',
				...options.headers,
				Authorization: `Bearer ${getOAuthToken()}`
			}
		});

	let response = await makeRequest();

	// If we get 401 (Unauthorized), the token might be expired - refresh and retry once
	if (response.status === 401) {
		const refreshed = await refreshOAuthToken();
		if (refreshed) {
			// Retry the request with the new token
			response = await makeRequest();
		}
	}

	return response;
};

// ---------------------------------------------------------------------------
// Shared / reusable types
// ---------------------------------------------------------------------------

/** Shape returned by the backend for a single persisted node-type record. */
export interface NodeTypeRecord {
	id: string;
	node_id: number;
	tenant_id: number;
	node_type: string;
	node_type_source: string;
	assigned_by: string | null;
	created_at: string;
	updated_at: string;
}

/** Shape returned by the dropdown-defaults endpoints. */
export interface DropdownDefault {
	nodeId: number;
	name: string;
	level: number;
	isTenant: boolean;
	isProperty: boolean;
	isNew: boolean;
	allowedNodeTypes: string[];
	defaultNodeType: string | null;
	currentNodeType: string | null;
	currentSource: string | null;
}

// ---------------------------------------------------------------------------
// 1. Assign Label to a Single Node — POST /assign
// ---------------------------------------------------------------------------

export interface AssignHierarchyLabelRequest {
	nodeId: number;
	nodeType: HierarchyLabelType;
	assignedBy?: string;
	source?: 'explicit' | 'system_default';
}

export interface AssignHierarchyLabelResponse {
	success: boolean;
	data?: NodeTypeRecord;
	message?: string;
}

export const assignHierarchyLabel = async (
	payload: AssignHierarchyLabelRequest
): Promise<AssignHierarchyLabelResponse> => {
	const response = await orgDirectoryFetch(`${BASE_URL}/assign`, {
		method: 'POST',
		body: JSON.stringify(payload)
	});

	return response.json();
};

// ---------------------------------------------------------------------------
// 2. Bulk Assign Labels — POST /bulk-assign
// ---------------------------------------------------------------------------

export interface BulkAssignHierarchyLabelRequest {
	tenantId: number;
	nodeTypes: Array<{
		nodeId: number;
		nodeType: HierarchyLabelType;
	}>;
}

export interface BulkAssignHierarchyLabelResponse {
	success: boolean;
	message?: string;
	data?: {
		success: boolean;
		assigned: number;
		failed: number;
		results: Array<{
			nodeId: number;
			nodeType: string;
			success: boolean;
			error?: string;
		}>;
	};
}

export const bulkAssignHierarchyLabel = async (
	payload: BulkAssignHierarchyLabelRequest
): Promise<BulkAssignHierarchyLabelResponse> => {
	const response = await orgDirectoryFetch(`${BASE_URL}/bulk-assign`, {
		method: 'POST',
		body: JSON.stringify(payload)
	});

	return response.json();
};

// ---------------------------------------------------------------------------
// 2b. Bulk Assign Labels (Nested) — POST /bulk-assign-nested
// ---------------------------------------------------------------------------

export interface NestedNodeTypeAssignment {
	id: number;
	name: string;
	parentId: number | null;
	rootGroupId: number;
	level: number;
	nodeType: HierarchyLabelType;
	source?: 'explicit' | 'system_default';
	assignedBy?: string;
}

export interface BulkAssignNestedHierarchyLabelRequest {
	rootGroupId: number;
	nodeTypes: NestedNodeTypeAssignment[];
}

export interface BulkAssignNestedHierarchyLabelResponse {
	success: boolean;
	message?: string;
	data?: {
		success: boolean;
		assigned: number;
		failed: number;
		tenantId: number;
		results: Array<{
			nodeId: number;
			nodeType: string;
			success: boolean;
			error?: string;
			warning?: string;
		}>;
	};
}

export const bulkAssignNestedHierarchyLabel = async (
	payload: BulkAssignNestedHierarchyLabelRequest
): Promise<BulkAssignNestedHierarchyLabelResponse> => {
	const response = await orgDirectoryFetch(`${BASE_URL}/bulk-assign-nested`, {
		method: 'POST',
		body: JSON.stringify(payload)
	});

	return response.json();
};

// ---------------------------------------------------------------------------
// 3. Get Dropdown Defaults for a Single Node — GET /node/{nodeId}/dropdown-defaults
// ---------------------------------------------------------------------------

export interface GetDropdownDefaultsResponse {
	success: boolean;
	data?: DropdownDefault;
	message?: string;
}

export const getDropdownDefaults = async (
	nodeId: number
): Promise<GetDropdownDefaultsResponse> => {
	const response = await orgDirectoryFetch(
		`${BASE_URL}/node/${nodeId}/dropdown-defaults`,
		{ method: 'GET' }
	);

	return response.json();
};

// ---------------------------------------------------------------------------
// 4. Get Dropdown Defaults for ALL Nodes in a Tenant — GET /tenant/{tenantId}/dropdown-defaults
// ---------------------------------------------------------------------------

export interface GetTenantDropdownDefaultsResponse {
	success: boolean;
	count?: number;
	data?: DropdownDefault[];
	message?: string;
}

export const getTenantDropdownDefaults = async (
	tenantId: number
): Promise<GetTenantDropdownDefaultsResponse> => {
	const response = await orgDirectoryFetch(
		`${BASE_URL}/tenant/${tenantId}/dropdown-defaults`,
		{ method: 'GET' }
	);

	return response.json();
};

// ---------------------------------------------------------------------------
// 5. Get Approved Node Type Values — GET /values
// ---------------------------------------------------------------------------

export interface GetNodeTypeValuesResponse {
	success: boolean;
	data?: string[];
	message?: string;
}

export const getNodeTypeValues = async (): Promise<GetNodeTypeValuesResponse> => {
	const response = await orgDirectoryFetch(`${BASE_URL}/values`, {
		method: 'GET'
	});

	return response.json();
};

// ---------------------------------------------------------------------------
// 6. Get Approved Tenant Node Type Values — GET /tenant/node-type-values
// ---------------------------------------------------------------------------

export interface GetTenantNodeTypeValuesResponse {
	success: boolean;
	data?: string[];
	message?: string;
}

export const getTenantNodeTypeValues = async (): Promise<GetTenantNodeTypeValuesResponse> => {
	const response = await orgDirectoryFetch(
		`${BASE_URL}/tenant/node-type-values`,
		{ method: 'GET' }
	);

	return response.json();
};

// ---------------------------------------------------------------------------
// 7. Get Node Type for a Specific Node — GET /node/{nodeId}
// ---------------------------------------------------------------------------

export interface GetHierarchyLabelResponse {
	success: boolean;
	data?: NodeTypeRecord;
	message?: string;
}

export const getHierarchyLabel = async (
	nodeId: number
): Promise<GetHierarchyLabelResponse> => {
	const response = await orgDirectoryFetch(`${BASE_URL}/node/${nodeId}`, {
		method: 'GET'
	});

	return response.json();
};

// ---------------------------------------------------------------------------
// 8. Get All Node Types for a Tenant — GET /tenant/{tenantId}
// ---------------------------------------------------------------------------

export interface GetTenantNodeTypesResponse {
	success: boolean;
	count?: number;
	data?: NodeTypeRecord[];
	message?: string;
}

export const getTenantNodeTypes = async (
	tenantId: number
): Promise<GetTenantNodeTypesResponse> => {
	const response = await orgDirectoryFetch(`${BASE_URL}/tenant/${tenantId}`, {
		method: 'GET'
	});

	return response.json();
};

// ---------------------------------------------------------------------------
// 9. Delete Node Type for a Specific Node (Group or Tenant) — DELETE /node/{nodeId}
// ---------------------------------------------------------------------------

export interface DeleteHierarchyLabelResponse {
	success: boolean;
	message?: string;
	deletedNodeType?: string;
}

export const deleteHierarchyLabel = async (
	nodeId: number
): Promise<DeleteHierarchyLabelResponse> => {
	const response = await orgDirectoryFetch(`${BASE_URL}/node/${nodeId}`, {
		method: 'DELETE'
	});

	return response.json();
};
